import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HistoryIcon from '@mui/icons-material/History';
import { useInboxApi } from 'src/modules/inbox/api';

type PhoneOption = {
  group: 'Contacts' | 'Previous tests';
  contactId: string | null;
  phoneNumber: string;
  label: string;
};

type PhoneTypeaheadProps = {
  companyId: string;
  recentPhones: string[];
  value: string;
  onChange: (phone: string, contactId: string | null) => void;
};

const digitsOf = (value: string): string => value.replace(/\D/g, '');

export const limitPhoneDigits = (value: string): string => {
  const digits = digitsOf(value);
  const maxDigits = digits.startsWith('1') ? 11 : 10;
  if (digits.length <= maxDigits) return value;

  let digitCount = 0;
  return [...value].filter(character => {
    if (!/\d/.test(character)) return true;
    digitCount += 1;
    return digitCount <= maxDigits;
  }).join('');
};

const contactName = (contact: { firstName?: string | null; lastName?: string | null }): string =>
  `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();

export function PhoneTypeahead({ companyId, recentPhones, value, onChange }: PhoneTypeaheadProps) {
  const { getContacts } = useInboxApi();
  const [input, setInput] = useState(value);
  const [selectedContactName, setSelectedContactName] = useState('');
  const [contactOptions, setContactOptions] = useState<PhoneOption[]>([]);
  const [recentContacts, setRecentContacts] = useState<Record<string, { contactId: string; label: string }>>({});
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingRecentNames, setLoadingRecentNames] = useState(false);

  // Resolve names for saved test numbers live (never persisted) so recents can show who they belong to.
  useEffect(() => {
    if (!companyId || recentPhones.length === 0) {
      setRecentContacts({});
      setLoadingRecentNames(false);
      return;
    }
    let cancelled = false;
    setLoadingRecentNames(true);
    (async () => {
      const entries = await Promise.all(
        recentPhones.map(async phone => {
          try {
            const response = await getContacts({ companyId, query: digitsOf(phone), limit: 5, offset: 0 });
            const match = response.data.items.find(item => digitsOf(item.contact.phoneNumber) === digitsOf(phone));
            return [phone, match ? { contactId: match.contact.id, label: contactName(match.contact) } : null] as const;
          } catch {
            return [phone, null] as const;
          }
        }),
      );
      if (!cancelled) {
        setRecentContacts(Object.fromEntries(entries.filter((entry): entry is readonly [string, { contactId: string; label: string }] => entry[1] !== null)));
        setLoadingRecentNames(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recentPhones, companyId, getContacts]);

  useEffect(() => {
    const query = input.trim();
    if (query.length < 2) {
      setContactOptions([]);
      setLoadingContacts(false);
      return;
    }
    let cancelled = false;
    setLoadingContacts(true);
    const timer = setTimeout(async () => {
      try {
        const response = await getContacts({ companyId, query, limit: 5, offset: 0 });
        if (!cancelled) {
          setContactOptions(
            response.data.items.map(item => ({
              group: 'Contacts' as const,
              contactId: item.contact.id,
              phoneNumber: item.contact.phoneNumber,
              label: contactName(item.contact) || item.contact.phoneNumber,
            })),
          );
        }
      } catch {
        if (!cancelled) setContactOptions([]);
      } finally {
        if (!cancelled) setLoadingContacts(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [input, companyId, getContacts]);

  const options = useMemo<PhoneOption[]>(() => {
    const query = input.trim().toLocaleLowerCase();
    const digits = digitsOf(query);
    const recents: PhoneOption[] = recentPhones
      .filter(phone => {
        const recentContactName = recentContacts[phone]?.label.toLocaleLowerCase() ?? '';
        return query === ''
          || (digits !== '' && digitsOf(phone).includes(digits))
          || recentContactName.includes(query);
      })
      .map(phone => ({
        group: 'Previous tests' as const,
        contactId: recentContacts[phone]?.contactId ?? null,
        phoneNumber: phone,
        label: recentContacts[phone]?.label || phone,
      }));
    return [...contactOptions, ...recents];
  }, [contactOptions, recentPhones, recentContacts, input]);

  const isLoading = loadingContacts || loadingRecentNames;

  return (
    <Autocomplete
      freeSolo
      autoComplete={false}
      loading={isLoading}
      loadingText="Loading phones…"
      options={options}
      groupBy={option => (typeof option === 'string' ? '' : option.group)}
      getOptionLabel={option => (typeof option === 'string' ? option : option.phoneNumber)}
      getOptionDisabled={option => typeof option !== 'string' && !option.contactId}
      filterOptions={opts => opts}
      inputValue={input}
      onInputChange={(_, newValue, reason) => {
        const limitedValue = limitPhoneDigits(newValue);
        setInput(limitedValue);
        if (reason === 'input') {
          setSelectedContactName('');
          onChange(limitedValue, null);
        }
      }}
      onChange={(_, option) => {
        const phone = limitPhoneDigits(typeof option === 'string' ? option : option?.phoneNumber ?? '');
        const name = typeof option === 'string' || !option?.contactId || option.label === option.phoneNumber
          ? ''
          : option.label;
        setInput(phone);
        setSelectedContactName(name);
        onChange(phone, typeof option === 'string' ? null : option?.contactId ?? null);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.group}-${option.phoneNumber}`} sx={{ display: 'flex', gap: 1 }}>
          {option.group === 'Contacts'
            ? <PersonOutlineIcon fontSize="small" color="primary" />
            : <HistoryIcon fontSize="small" color="disabled" />}
          <Box>
            <Typography variant="body2">{option.label}</Typography>
            {option.label !== option.phoneNumber && (
              <Typography variant="caption" color="text.secondary">{option.phoneNumber}</Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label="Phone Number"
          placeholder="Enter or search a number"
          fullWidth
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password',
            inputMode: 'tel',
            name: undefined,
            'data-form-type': 'other',
            'data-lpignore': 'true',
            'data-1p-ignore': 'true',
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {selectedContactName && (
                  <Typography
                    variant="body2"
                    color="success.main"
                    noWrap
                    sx={{ maxWidth: 180, mr: 1 }}
                    title={selectedContactName}
                  >
                    {selectedContactName}
                  </Typography>
                )}
                {isLoading && <CircularProgress color="inherit" size={20} aria-label="Loading phones" />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
